import { useRef } from 'react';
import type { MerchantSlot } from '../../services/merchantSlots.api';
import { slotStyle } from '../../services/merchantSlots.api';
import { useTimelineData, DAY_START_HOUR } from './useTimelineData';
import DensityBar from './DensityBar';
import TimelineBar from './TimelineBar';

const TOTAL_WIDTH = 1440; // 1px per minute
const ROW_HEIGHT = 32;
// Hours starting from DAY_START_HOUR (7) → 7,8,...,23,0,1,...,6
const HOURS = Array.from({ length: 24 }, (_, i) => (DAY_START_HOUR + i) % 24);

interface TimelineViewProps {
  merchants: MerchantSlot[];
}

export default function TimelineView({ merchants }: TimelineViewProps) {
  const {
    rows, densityBuckets, peakOverlap, totalMerchants,
    busiestSlot, slotCounts, dedicatedCount, unsetCount,
  } = useTimelineData(merchants);
  const scrollRef = useRef<HTMLDivElement>(null);
  const namesRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (scrollRef.current && namesRef.current) {
      namesRef.current.scrollTop = scrollRef.current.scrollTop;
    }
  };

  const statCards = [
    { label: 'On a slot', value: totalMerchants, dotColor: 'bg-purple-500' },
    ...slotCounts.map((s) => ({ label: s.code, value: s.count, dotColor: slotStyle(s.code).bar })),
    // Merchants with no slot draw no bar; counting them here is the only thing
    // stopping them disappearing from this view entirely.
    { label: 'No slot', value: unsetCount, dotColor: 'bg-gray-300' },
    { label: 'Dedicated', value: dedicatedCount, dotColor: 'bg-orange-400' },
    { label: 'Peak Overlap', value: peakOverlap, dotColor: 'bg-amber-500' },
    { label: 'Busiest', value: busiestSlot, dotColor: 'bg-blue-500' },
  ];

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="flex flex-wrap gap-2">
        {statCards.map((s) => (
          <div key={s.label} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
            <span className={`w-2.5 h-2.5 rounded-full ${s.dotColor} flex-shrink-0`} />
            <span className="text-xs text-gray-500">{s.label}</span>
            <span className="text-sm font-bold text-gray-900">{s.value}</span>
          </div>
        ))}
      </div>

      {/* Density heatmap — full width */}
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="text-xs font-medium text-gray-500 mb-2">Overlap Density</div>
        <DensityBar buckets={densityBuckets} peak={peakOverlap} fullWidth />
      </div>

      {/* Color legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
        <span className="font-medium">Slot:</span>
        {slotCounts.map((s) => (
          <span key={s.code} className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded inline-block ${slotStyle(s.code).bar}`} />
            {s.code} {s.window}
            {s.label ? ` · ${s.label}` : ''}
          </span>
        ))}
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-300 ring-2 ring-orange-400 inline-block" /> Dedicated</span>
      </div>

      {/* Timeline */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <div className="flex">
          {/* Names column header */}
          <div className="w-48 flex-shrink-0 bg-gray-50 border-r border-gray-200 px-3 py-2 text-xs font-medium text-gray-500 uppercase">
            Merchant
          </div>
          {/* Hour axis */}
          <div className="overflow-hidden flex-1">
            <div className="flex" style={{ width: TOTAL_WIDTH }}>
              {HOURS.map((h, idx) => (
                <div
                  key={idx}
                  className="text-[10px] text-gray-500 border-l border-gray-200 px-0.5"
                  style={{ width: 60 }}
                >
                  {String(h).padStart(2, '0')}:00
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex" style={{ maxHeight: 600 }}>
          {/* Names column */}
          <div
            ref={namesRef}
            className="w-48 flex-shrink-0 border-r border-gray-200 overflow-hidden"
          >
            {rows.map(row => (
              <div
                key={row.merchantId}
                className="px-3 text-xs text-gray-700 truncate border-b border-gray-100 flex items-center"
                style={{ height: ROW_HEIGHT }}
                title={row.merchantName}
              >
                {row.merchantName}
              </div>
            ))}
          </div>

          {/* Scrollable grid */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-auto"
            onScroll={handleScroll}
          >
            <div style={{ width: TOTAL_WIDTH }}>
              {rows.map(row => (
                <div
                  key={row.merchantId}
                  className="relative border-b border-gray-100"
                  style={{ height: ROW_HEIGHT }}
                >
                  {/* Hour gridlines */}
                  {HOURS.map((_h, idx) => (
                    <div
                      key={idx}
                      className="absolute top-0 bottom-0 border-l border-gray-100"
                      style={{ left: idx * 60 }}
                    />
                  ))}
                  {/* Bars */}
                  {row.bars.map((bar, i) => (
                    <TimelineBar key={i} bar={bar} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
